import backendClient from "@/api/backendClient";

import type {
  EntityTreeNode,
  MaintenanceExportQueryParams,
  MaintenanceExtended,
  MaintenanceFinish,
  MaintenanceFollowupDuration,
  MaintenanceFromStrategyCreate,
  MaintenanceInsert,
  MaintenanceItemExportQueryParams,
  MaintenanceListQueryParams,
  MaintenanceSkip,
  MaintenanceUpcomingVirtualQueryParams,
  MaintenanceUpdate,
} from "@waku-care/types/contracts/internal";

import type { RawMaintenance } from "@/api/resources/maintenances/types";

/** ! MAINTENANCE FILTERING BY VIEW:
 * HISTORY:
 * - all maintenances COMPLETED
 * - all maintenances SKIPPED
 * OPEN:
 * - all maintenances OVERDUE (planned_at OR scheduled_at <= startOfToday)
 * - all maintenances TODAY (planned_at OR scheduled_at BETWEEN startOfToday <-> endOfToday)
 * - all maintenances STARTED (started_at <= endOfToday) (NOTE: started_at can't be higher than NOW)
 * UPCOMING:
 * - all maintenances TODAY (planned_at OR scheduled_at BETWEEN startOfToday <-> endOfToday)
 * - all maintenances STARTED (started_at >= startOfToday) (NOTE: started_at can't be higher than NOW)
 * - all maintenances SCHEDULED (planned_at OR scheduled_at >= startOfToday)
 * - all virtual maintenances (effectively scheduled_at >= startOfToday)
 *
 * OVERLAP OPEN + UPCOMING:
 * - OPEN (all maintenances STARTED) + (all maintenances TODAY)
 * - UPCOMING (all maintenances STARTED) + (all maintenances TODAY)
 */

export const getMaintenance = async ({ id }: { id: string }) => {
  const { data, error } = await backendClient.get<MaintenanceExtended>(`/maintenances/${id}`);
  if (error) throw error;
  return data;
};

// maintenances by id, regardless of their workspace (eg. chart drill-downs)
export const getMaintenancesByIds = async (ids: string[]) => {
  if (ids.length === 0) return [];
  const { data, error } = await backendClient.get<MaintenanceExtended[]>(`/maintenances/by-ids`, { params: { ids } });
  if (error) throw error;
  return data;
};

export interface ListMaintenancesByWorkspaceIdProps {
  workspace_id?: string;
  params?: MaintenanceListQueryParams;
}
export const getMaintenancesByWorkspaceId = async ({ workspace_id, params }: ListMaintenancesByWorkspaceIdProps) => {
  const { data, count, error } = await backendClient.get<MaintenanceExtended[]>(
    `/maintenances/by-workspace/${workspace_id}`,
    { params },
  );
  if (error) throw error;
  return { data, count };
};

export interface ListMaintenancesUpcomingVirtualByWorkspaceIdProps {
  workspace_id?: string;
  params: MaintenanceUpcomingVirtualQueryParams;
}
export const getMaintenancesUpcomingVirtualByWorkspaceId = async ({
  workspace_id,
  params,
}: ListMaintenancesUpcomingVirtualByWorkspaceIdProps) => {
  const { data, count, error } = await backendClient.get<MaintenanceExtended[]>(
    `/maintenances/upcoming-virtual/by-workspace/${workspace_id}`,
    { params },
  );
  if (error) throw error;
  return { data, count };
};

export const createMaintenance = async (values: MaintenanceInsert) => {
  const { data, error } = await backendClient.post<MaintenanceExtended>(`/maintenances`, { body: values });
  if (error) throw error;
  return data;
};

export const createMaintenanceFromMaintenanceStrategy = async (
  values: MaintenanceFromStrategyCreate,
): Promise<RawMaintenance | null> => {
  const { data, error } = await backendClient.post<RawMaintenance>(`/maintenances/from-strategy`, { body: values });
  if (error) throw error;
  return data;
};

export const updateMaintenance = async ({ id, values }: { id: string; values: MaintenanceUpdate }) => {
  const { data, error } = await backendClient.patch<MaintenanceExtended>(`/maintenances/${id}`, { body: values });
  if (error) throw error;
  return data;
};

export const deleteMaintenance = async ({ id }: { id: string }) => {
  const { error } = await backendClient.delete<null>(`/maintenances/${id}`);
  if (error) throw error;
  return null;
};

// starts a scheduled maintenance, assigning the current user
export const startMaintenance = async ({ id }: { id: string }) => {
  const { data, error } = await backendClient.post<MaintenanceExtended>(`/maintenances/${id}/start`);
  if (error) throw error;
  return data;
};

export const skipMaintenance = async ({ id, values }: { id: string; values: MaintenanceSkip }) => {
  const { data, error } = await backendClient.post<MaintenanceExtended>(`/maintenances/${id}/skip`, { body: values });
  if (error) throw error;
  return data;
};

export const finishMaintenance = async ({ id, values }: { id: string; values: MaintenanceFinish }) => {
  const { data, error } = await backendClient.post<MaintenanceExtended>(`/maintenances/${id}/finish`, {
    body: values,
  });
  if (error) throw error;
  return data;
};

export const getMaintenanceTree = async (id: string) => {
  const { data, error } = await backendClient.get<EntityTreeNode[]>(`/maintenances/${id}/tree`);
  if (error) throw error;
  return data;
};

export const getMaintenanceFollowupDuration = async (id: string) => {
  const { data, error } = await backendClient.get<MaintenanceFollowupDuration>(`/maintenances/${id}/followup-duration`);
  if (error) throw error;
  return data;
};

// CSV export of the workspace's maintenances, as CSV text
export const exportMaintenancesCsv = async ({
  workspace_id,
  params,
}: {
  workspace_id: string;
  params: MaintenanceExportQueryParams;
}) => {
  const { data, error } = await backendClient.get<string>(`/maintenances/export/by-workspace/${workspace_id}`, {
    params,
  });
  if (error) throw error;
  return data;
};

// CSV export of the workspace's maintenance items (optionally incl. device logs), as CSV text
export const exportMaintenanceItemsCsv = async ({
  workspace_id,
  params,
}: {
  workspace_id: string;
  params: MaintenanceItemExportQueryParams;
}) => {
  const { data, error } = await backendClient.get<string>(`/maintenances/export-items/by-workspace/${workspace_id}`, {
    params,
  });
  if (error) throw error;
  return data;
};
